import React, { useState } from "react";
import { Box, Collapse, Typography } from "@mui/material";
import { ExpandLessRounded, ExpandMoreRounded } from "@mui/icons-material";
import { T, SERIES_SOFT } from "utils/backrrTokens";
import BarRow from "./BarRow";

// Category rows with their sectors nested underneath. This is the one place
// the sequential ramp earns its keep: the parent takes a darker step and its
// sectors a lighter one, encoding real nesting depth rather than decoration.
//
// Borderless — BreakdownPanel owns the surface and the hairlines.
const CategorySectorBreakdown = ({ block, isLoading }) => {
  const [open, setOpen] = useState({});

  if (isLoading) return <Box className="skeleton" sx={{ height: "14rem", margin: "12px", borderRadius: "8px" }} />;

  const rows = block?.rows ?? [];
  const max = rows.reduce((m, r) => Math.max(m, r.count), 0);
  const anyNested = rows.some((r) => r.sectors?.length > 0);

  return (
    <Box sx={{ padding: "12px 8px", minWidth: 0 }}>
      <Typography
        sx={{ fontSize: "0.85rem", fontWeight: 600, paddingLeft: "8px", marginBottom: 0.75 }}
        color={T.ink}
      >
        Category &amp; Sector
      </Typography>

      {rows.length === 0 ? (
        <Typography padding={3} textAlign="center" fontSize="0.78rem" color={T.inkSubtle}>
          No startups in this range.
        </Typography>
      ) : (
        <>
          {rows.map((row) => {
            const hasChildren = row.sectors?.length > 0;
            const expanded = Boolean(open[row.label]);
            const Chevron = expanded ? ExpandLessRounded : ExpandMoreRounded;

            return (
              <Box key={row.label}>
                <BarRow
                  label={row.label}
                  count={row.count}
                  max={max}
                  color={SERIES_SOFT[0]}
                  onClick={hasChildren ? () => setOpen((s) => ({ ...s, [row.label]: !s[row.label] })) : undefined}
                  trailing={
                    hasChildren ? (
                      <Chevron
                        titleAccess={expanded ? "Collapse sectors" : "Expand sectors"}
                        sx={{
                          position: "relative",
                          fontSize: "0.95rem",
                          color: T.inkSubtle,
                          flexShrink: 0,
                        }}
                      />
                    ) : null
                  }
                />
                <Collapse in={expanded}>
                  {(row.sectors ?? []).map((sector) => (
                    <BarRow
                      key={sector.label}
                      label={sector.label}
                      count={sector.count}
                      max={max}
                      color={SERIES_SOFT[1]}
                      indent={1}
                      muted
                    />
                  ))}
                </Collapse>
              </Box>
            );
          })}
          <Typography
            sx={{ fontSize: "0.72rem", paddingLeft: "8px", marginTop: 1 }}
            color={T.inkSubtle}
          >
            {block?.total ?? 0} startups · may hold more than one
            {anyNested ? " · select a category for sectors" : ""}
          </Typography>
        </>
      )}
    </Box>
  );
};

export default CategorySectorBreakdown;
